import React from 'react';
import { styled } from '@compiled/react';
import Button, { LoadingButton } from '@atlaskit/button';
import Modal, { ModalTitle } from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import {
	type UIAnalyticsEvent,
	FireScreenAnalytics,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useGetJswPremiumBillingUrl } from '../../services/jsw-premium-billing-url';
import illustration from './assets/edition-awareness-illustration.svg';
import BenefitsList from './benefits-list';
import messages from './messages';

export type FreeToPremiumModalProps = {
	onClose: () => void;
	billingSystem: string;
	isProjectSimplified: boolean;
};

export const FreeToPremiumModal = ({
	onClose,
	billingSystem,
	isProjectSimplified,
}: FreeToPremiumModalProps) => {
	const { formatMessage } = useIntl();
	const { data: billingPageData } = useGetJswPremiumBillingUrl();

	const openBillingPage = (
		e: React.MouseEvent<HTMLElement, MouseEvent>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		e.preventDefault();

		if (billingPageData?.billingPageUrl) {
			window.open(billingPageData.billingPageUrl, '_blank', 'noreferrer');
			onClose();
		}
		fireUIAnalytics(analyticsEvent, 'jwmMonetisationModalTryNow', {
			projectType: isProjectSimplified ? 'TMP' : 'CMP',
			billingSystem,
		});
	};

	return (
		<Modal
			onClose={onClose}
			width={720}
			autoFocus={false}
			shouldScrollInViewport
			testId="business-trial-banner.ui.modal.free-to-premium-modal"
		>
			<FireScreenAnalytics />
			<BodyContainer>
				<CoverImage />
				<BodyPanel>
					<ModalTitle>{formatMessage(messages.tryAcrossProductsSpork)}</ModalTitle>
					<p>
						{formatMessage(messages.startATrialSpork, {
							b: (chunks: React.ReactNode[]) => <b>{chunks}</b>,
						})}
					</p>

					<BenefitsList />

					<ButtonGroupContainer>
						<Button onClick={onClose}>{formatMessage(messages.maybeLater)}</Button>
						<LoadingButton
							appearance="primary"
							isLoading={billingPageData?.billingPageUrl === undefined}
							href={billingPageData?.billingPageUrl}
							onClick={openBillingPage}
							target="_blank"
						>
							{formatMessage(messages.tryPremium)}
						</LoadingButton>
					</ButtonGroupContainer>
				</BodyPanel>
			</BodyContainer>
		</Modal>
	);
};

export default FreeToPremiumModal;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BodyContainer = styled.div({
	display: 'flex',
	position: 'relative',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CoverImage = styled.div({
	flex: '0 1',
	flexBasis: '235px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	background: `url(${illustration}) no-repeat center center / 100% 100%`,
	boxSizing: 'border-box',
	// This border radius makes sure the image is rounded on the left side
	// so it fits into the modal container which is also rounded by the same amount.
	borderRadius: '3px 0 0 3px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BodyPanel = styled.div({
	flex: '1',
	flexBasis: '480px',
	padding: token('space.500', '40px'),
	boxSizing: 'border-box',
	color: token('color.text', '#172B4D'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonGroupContainer = styled.div({
	display: 'flex',
	justifyContent: 'right',
	gap: token('space.150', '12px'),
});
